import { type ISurveyCreator, SurveyRow } from "survey-react-ui";
import type { PanelModel, SurveyModel } from "survey-core";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@rumbledirect/ui-v2";
import { cn } from "@rumbledirect/ui-v2/cn";
import { useSurveyQuestionRerender } from "../questions/useSurveyQuestionRerender";

/**
 * Renderer for SurveyJS `panel` elements. Maps to admin-v2's `Card` — the
 * same grouping the rest of admin-v2 uses for a titled set of fields. The
 * `ReactElementFactory` hands us `{ element, creator, survey, css }` (not
 * `{ panel }`): `element` is the panel model.
 *
 * Content is the panel's own `visibleRows`, each rendered by survey-react-ui's
 * `SurveyRow` (keyed by row id, exactly like its own `SurveyPanelBase`
 * renders panels). SurveyJS owns the row lifecycle: `startWithNewLine`
 * groupings are the rows, and `visibleIf` transitions rebuild the row/element
 * visibility on the model, which `useSurveyQuestionRerender` re-renders on.
 * The rows' layout (responsive flex, per-element widths) is applied to the
 * row element itself by `applySurveyRowLayout`, hooked onto the model in
 * `SurveyForm`.
 */
export function SurveyPanel({
  element,
  creator,
}: {
  readonly element: PanelModel;
  readonly creator: ISurveyCreator;
}) {
  useSurveyQuestionRerender(element);
  const panel = element;
  const description = panel.hasDescription
    ? panel.locDescription.renderedHtml
    : undefined;

  return (
    <Card className="mb-6">
      {panel.title || description ? (
        <CardHeader>
          {panel.title ? (
            <CardTitle>{panel.locTitle.renderedHtml}</CardTitle>
          ) : null}
          {description ? (
            <CardDescription>{description}</CardDescription>
          ) : null}
        </CardHeader>
      ) : null}
      <CardContent
        className={cn((panel.title || description) && "pt-5 md:pt-0")}
      >
        {panel.visibleRows.map((row) => (
          <SurveyRow
            key={row.id}
            row={row}
            survey={panel.survey as SurveyModel}
            creator={creator}
            css={panel.cssClasses}
          />
        ))}
      </CardContent>
    </Card>
  );
}
